(() => {
  'use strict';

  const ownedForms = new WeakMap();
  const usedNames = new Set();
  const pendingForms = new Set();
  let nextName = 0;
  let scheduled = false;

  document.querySelectorAll('form[toolname]').forEach(form => usedNames.add(form.getAttribute('toolname')));

  function describeForm(form) {
    if (form.matches('[data-filter-form]')) {
      return { name: 'filter_catalog', description: 'Filter the current product or search results using the available filter choices and price range. Keep the existing search term. Filters are applied by the existing shop interface.' };
    }
    if (form.matches('.klaviyo-form,[data-testid^="klaviyo-form-"]')) {
      const placement = form.closest('.custom-footer__klaviyo-form') ? 'footer' : 'displayed';
      return {
        name: 'klaviyo_signup',
        description: `Complete the ${placement} Klaviyo newsletter or promotional sign-up step using the displayed fields and its existing button. A later step may require additional information or consent.`,
      };
    }

    const action = form.getAttribute('action');
    if (!action) return null;
    let url;
    try { url = new URL(action, window.location.href); } catch { return null; }
    if (url.origin !== window.location.origin) return null;

    if (/\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(url.pathname)) {
      return { name: 'search_shop', description: 'Search the Duschbrocken shop using the search term and display matching results.' };
    }
    if (/\/cart\/add\/?$/.test(url.pathname)) {
      const product = form.getAttribute('data-product-handle');
      const buttonOnly = !form.querySelector('button[type="submit"],input[type="submit"],button:not([type])');
      return {
        name: 'add_to_cart',
        description: `Add ${product ? `the product ${product}` : 'the product associated with this form'} to the shopping cart with the selected variant and quantity. This does not place an order.${buttonOnly ? ' Use the existing add button; the product variant and quantity are fixed by this offer.' : ''}`,
      };
    }
    if (/\/localization\/?$/.test(url.pathname)) {
      return { name: 'set_shop_locale', description: 'Change the shop country or language using the available country and language choices. This may change currency, prices and shipping options and reload the page.' };
    }
    return null;
  }

  function describeField(field) {
    const name = field.name || '';
    if (name === 'q') return 'Search term for products and shop content.';
    if (name === 'id') return 'Shopify product variant ID. Use an available variant belonging to this product; a hidden value is fixed by this offer.';
    if (name === 'quantity') return 'Number of units to add to the cart, subject to this field\'s minimum and maximum.';
    if (name === 'country_code') return 'Country code from the shop\'s available country choices.';
    if (name === 'locale_code' || name === 'language_code') return 'Language code from the shop\'s available language choices.';
    if (name === 'options[prefix]') return 'Existing search prefix matching setting; keep this fixed value.';
    if (name === 'filter.v.price.gte') return 'Minimum product price in the current shop currency.';
    if (name === 'filter.v.price.lte') return 'Maximum product price in the current shop currency.';
    if (name.startsWith('filter.')) return 'Product filter. Select an available choice in this filter group.';
    if (/^options\[/.test(name)) return 'Product option. Choose one of the available values for this product.';
    if (/^properties\[/.test(name)) return 'Product personalization or additional product information for this cart item.';
    if (field.type === 'email') return 'Email address for this newsletter or promotional sign-up.';
    if (field.type === 'tel') return 'Phone number for the displayed sign-up step.';
    return field.getAttribute('aria-label') || [...(field.labels || [])].map(label => label.textContent.trim()).filter(Boolean).join(' ') || field.getAttribute('placeholder') || '';
  }

  function annotate(form) {
    if (!form.isConnected) return;
    if (!ownedForms.has(form) && (form.hasAttribute('toolname') || form.hasAttribute('tooldescription'))) return;
    const definition = describeForm(form);
    if (!definition) return;

    for (const field of form.elements) {
      if (!field.matches('input,select,textarea') || field.hasAttribute('toolparamdescription')) continue;
      const description = describeField(field);
      if (description) field.setAttribute('toolparamdescription', description);
    }

    if (!ownedForms.has(form)) {
      let name;
      do { name = `duschbrocken_${definition.name}_${++nextName}`; } while (usedNames.has(name));
      usedNames.add(name);
      ownedForms.set(form, name);
    }
    const description = definition.description;
    const marker = !form.hasAttribute('toolname') || !form.hasAttribute('tooldescription')
      ? document.createComment('webmcp')
      : null;
    const focusedField = marker && form.contains(document.activeElement) ? document.activeElement : null;
    if (marker) form.replaceWith(marker);
    try {
      if (form.getAttribute('tooldescription') !== description) form.setAttribute('tooldescription', description);
      if (!form.hasAttribute('toolname')) form.setAttribute('toolname', ownedForms.get(form));
    } finally {
      if (marker) marker.replaceWith(form);
      if (focusedField && document.activeElement !== focusedField) focusedField.focus({ preventScroll: true });
    }
  }

  function collect(node, descendants = true) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const form = node.closest('form');
    if (form) pendingForms.add(form);
    if (node.form) pendingForms.add(node.form);
    if (!descendants) return;
    node.querySelectorAll('form').forEach(item => pendingForms.add(item));
    node.querySelectorAll('input[form],select[form],textarea[form]').forEach(field => {
      if (field.form) pendingForms.add(field.form);
    });
  }

  const observer = new MutationObserver(records => {
    for (const record of records) {
      collect(record.target, false);
      record.addedNodes.forEach(node => collect(node));
    }
    if (!pendingForms.size || scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      pendingForms.forEach(annotate);
      pendingForms.clear();
    });
  });

  document.querySelectorAll('form').forEach(annotate);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['action', 'name', 'type', 'form', 'data-testid', 'data-product-handle', 'data-filter-form'],
  });
})();
